import { Link } from 'react-router-dom';
import { Briefcase, Cpu, Plane, Globe2, ChevronRight, CheckCircle2 } from 'lucide-react';
import { usePageMeta, useFAQSchema } from '../../../hooks/useSEO';
import BreadcrumbSchema from '../../../components/seo/BreadcrumbSchema';
import { FAQSection, FinalCTA } from './DubaiCostOfLivingPage';

const FREE_ZONES = [
  { name: 'DIFC — Dubai International Financial Centre', icon: Briefcase, copy: 'The Middle East’s top financial hub — 250+ regulated firms (HSBC, Goldman Sachs, Mashreq, Tabby). Best for Fintech, Banking, Investment Analytics internships.' },
  { name: 'Dubai Internet City — Tech HQ Belt',            icon: Cpu,       copy: 'Microsoft, Oracle, IBM, Meta and Google MENA HQs. AI, cloud, SaaS engineering and product internships — 15 min Metro from DIAC.' },
  { name: 'Dubai Media City & Studio City',                icon: Globe2,    copy: 'Sky News Arabia, MBC, CNN, BBC, OSN — journalism, social media production, content marketing internships.' },
  { name: 'DMCC & JAFZA — Logistics & Trade',              icon: Plane,     copy: '22,000+ trade firms, DP World, Emirates SkyCargo. Supply chain, commodities trading, logistics analyst roles.' },
];

const PERMIT_STEPS = [
  'Get a part-time job offer from a UAE-licensed employer (free-zone employers preferred).',
  'University Student Services issues a No-Objection Certificate (NOC).',
  'Employer files the work-permit application with MoHRE — typical issuance 2–3 weeks, fee AED 150–300 (often borne by the employer).',
  'Once approved, you can legally work up to 20 hours/week during semester and full-time during the official semester break.',
];

const FAQS = [
  { q: 'How many hours can I work on a Dubai student visa?', a: 'Up to 20 hours per week during the semester and full-time during scheduled vacations, with a work permit issued by the UAE Ministry of Human Resources & Emiratisation (MoHRE). Universities in DIAC and Knowledge Park coordinate the paperwork.' },
  { q: 'Do free-zone internships count toward the work-hour cap?', a: 'Paid internships count toward the 20-hour cap, but unpaid academic-credit internships often do not (they fall under the academic placement scheme). Always confirm with your university student-services desk before signing.' },
  { q: 'What is the typical hourly pay for Indian students in Dubai (2026)?', a: 'Tech / SaaS interns: AED 40 – 75/hr. DIFC analyst interns: AED 50 – 90/hr. Hospitality F&B: AED 25 – 40/hr. Retail / Mall jobs: AED 22 – 35/hr. Median monthly take-home for a motivated student: AED 2,500 – 5,500.' },
  { q: 'Can I switch from a student visa to a work visa in Dubai after graduation?', a: 'Yes. You can convert directly inside the UAE — either through the new employer-sponsored work residency (2 or 5 years), the Green Visa (5-year self-sponsored for skilled workers), or the Job Seeker Visa (60/90/120 days, sponsor-free) while you finalise an offer.' },
];

export default function DubaiWorkRightsPage() {
  usePageMeta(
    'Dubai Student Work Rights & Free Zone Internships (2026) — 20-Hr Permit Guide',
    'Complete 2026 guide to the UAE student work permit: MoHRE 20-hour/week rules, DIFC / Internet City / Media City / DMCC internship pathways, hourly pay rates, and student-to-work-visa conversion.'
  );
  useFAQSchema(FAQS);

  return (
    <div className="zaf-edu-platform bg-white text-slate-900" data-testid="dubai-work-rights-page">
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://zafco.in/' },
        { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
        { name: 'Study in Dubai', url: 'https://zafco.in/study-abroad/dubai/' },
        { name: 'Student Work Rights', url: 'https://zafco.in/study-abroad/dubai/student-work-rights/' },
      ]} />

      <section className="bg-gradient-to-br from-[#0F1B2C] to-[#162538] text-white py-12 md:py-16">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <Link to="/study-abroad/dubai/" className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-4 hover:text-amber-300">
            <ChevronRight className="w-3 h-3 rotate-180" /> Dubai Hub
          </Link>
          <h1 className="text-3xl md:text-5xl font-bold font-syne leading-tight">Student Work Rights — Dubai 2026</h1>
          <p className="mt-4 text-slate-300 max-w-3xl leading-relaxed">The UAE&apos;s student work permit lets you legally earn <strong className="text-white">AED 2,500 – 5,500 / month</strong> from your first semester — and gives you real internship access to DIFC, Microsoft, IBM and Sky News. Here&apos;s exactly how the 20-hour rule works.</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold font-syne mb-2">The 20-Hour MoHRE Permit — Step by Step</h2>
          <p className="text-slate-600 mb-6">Issued by the Ministry of Human Resources &amp; Emiratisation. Paperwork is bundled into the UAE student-visa package for most KHDA-licensed campuses.</p>
          <ol className="space-y-3">
            {PERMIT_STEPS.map((s, i) => (
              <li key={s} className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex gap-4">
                <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-[#0F1B2C] flex items-center justify-center font-bold flex-shrink-0">{i + 1}</span>
                <p className="text-slate-700 leading-relaxed">{s}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-slate-50">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold font-syne mb-2">Where to Intern — Dubai Free Zones</h2>
          <p className="text-slate-600 mb-7">Dubai&apos;s free zones host the city&apos;s most credentialed multinationals. All four below are within 25 minutes of DIAC / Knowledge Park by Metro.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {FREE_ZONES.map((fz) => (
              <article key={fz.name} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#D4AF37]/60 transition-colors">
                <fz.icon className="w-7 h-7 text-[#D4AF37] mb-2" />
                <h3 className="font-bold font-syne text-[#0F1B2C] mb-2">{fz.name}</h3>
                <p className="text-sm text-slate-700 leading-relaxed">{fz.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <FAQSection faqs={FAQS} />
      <FinalCTA source="dubai-student-work-rights" />
    </div>
  );
}
